import { U } from './constants';
import type { ViewMode } from './constants';
import { state } from './state';
import { refs } from './refs';
import { renderer, scene, camera, canvas, wrap } from './scene';

// ─── Camera / rotation ─────────────────────────────────────────────
function radius(): number {
  const maxDim = Math.max(state.W, state.H, state.S) * U;
  return (maxDim * 3.4) / state.zoom;
}
export function applyCamera(): void {
  const r = radius();
  camera.position.x = state.panX + r * Math.sin(state.rotY) * Math.cos(state.rotX);
  camera.position.y = state.panY + r * Math.sin(state.rotX);
  camera.position.z = r * Math.cos(state.rotY) * Math.cos(state.rotX);
  camera.lookAt(state.panX, state.panY, 0);
}
export function applySpin(): void {
  if (refs.bookGroup) refs.bookGroup.rotation.y = state.bookSpin;
}

export function setViewMode(m: ViewMode): void {
  state.viewMode = m;
  if (m === 'turntable') {
    state.rotY = 0;
    state.rotX = 0.16; // camera locked front-elevated
    state.bookSpin = 0.55; // start at a hero ¾ spin
  } else {
    state.bookSpin = 0;
    state.rotX = 0.16;
    state.rotY = 0.62;
  }
  applySpin();
  applyCamera();
}

canvas.addEventListener('mousedown', e => {
  state.isDragging = true;
  state.isRight = e.button === 2;
  state.prevX = e.clientX;
  state.prevY = e.clientY;
  state.autoRot = false;
});
canvas.addEventListener('contextmenu', e => e.preventDefault());
window.addEventListener('mousemove', e => {
  if (!state.isDragging) return;
  const dx = e.clientX - state.prevX,
    dy = e.clientY - state.prevY;
  state.prevX = e.clientX;
  state.prevY = e.clientY;
  if (state.isRight) {
    state.panX -= dx * 0.003;
    state.panY += dy * 0.003;
    applyCamera();
  } else if (state.viewMode === 'turntable') {
    state.bookSpin += dx * 0.01;
    applySpin(); // left-right spins book
    state.rotX = Math.max(-1.3, Math.min(1.3, state.rotX - dy * 0.006));
    applyCamera(); // up-down tilts camera
  } else {
    state.rotY += dx * 0.008;
    state.rotX = Math.max(-1.3, Math.min(1.3, state.rotX - dy * 0.006));
    applyCamera();
  }
});
window.addEventListener('mouseup', () => (state.isDragging = false));
canvas.addEventListener(
  'wheel',
  e => {
    e.preventDefault();
    state.zoom = Math.max(0.3, Math.min(3.5, state.zoom - e.deltaY * 0.001));
    applyCamera();
  },
  { passive: false },
);

let lastTouch: Touch | null = null;
canvas.addEventListener(
  'touchstart',
  e => {
    lastTouch = e.touches[0];
    state.autoRot = false;
  },
  { passive: true },
);
canvas.addEventListener(
  'touchmove',
  e => {
    e.preventDefault();
    if (!lastTouch) return;
    const t = e.touches[0];
    const dx = t.clientX - lastTouch.clientX,
      dy = t.clientY - lastTouch.clientY;
    lastTouch = t;
    if (state.viewMode === 'turntable') {
      state.bookSpin += dx * 0.012;
      applySpin();
      state.rotX = Math.max(-1.3, Math.min(1.3, state.rotX - dy * 0.008));
      applyCamera();
    } else {
      state.rotY += dx * 0.01;
      state.rotX = Math.max(-1.3, Math.min(1.3, state.rotX - dy * 0.008));
      applyCamera();
    }
  },
  { passive: false },
);
canvas.addEventListener('touchend', () => (lastTouch = null));

// ─── Resize / render ───────────────────────────────────────────────
export function resize(): void {
  const w = wrap.clientWidth,
    h = wrap.clientHeight || 500;
  renderer.setSize(w, h);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
}
window.addEventListener('resize', resize);

export function animate(): void {
  requestAnimationFrame(animate);
  if (state.autoRot) {
    if (state.viewMode === 'turntable') {
      state.bookSpin += 0.0035;
      applySpin();
    } else {
      state.rotY += 0.0035;
      applyCamera();
    }
  }
  renderer.render(scene, camera);
}
